// Admin → Teams-kanaalsjabloon (docs/teams-kanaal-automatisch.md): de mappenboom die in elk nieuw
// projectkanaal komt, wie er altijd lid wordt, wie de "kies een team"-melding krijgt, de kanaalgrens —
// en de lijst van wat de automaat deed of in de proefstand zóu doen.
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { AI_ENGINE_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { bevestig } from '../../../utils/bevestig';
import { teamsKanaalStatusKleur, teamsKanaalStatusLabel } from '../../../utils/teamsKanaalStatus';

const API = `${AI_ENGINE_URL}/api/v1/teams/kanaal`;
const MODUS_TEKST = {
  off: 'Uit — er gebeurt niets.',
  dry: 'Proefstand — Toolframe berekent wat het zou doen, maar maakt niets aan en mailt niet.',
  on: 'Aan — kanalen, mappen en teamleden worden echt aangemaakt.',
};

const lijstNaarTekst = (l) => (l || []).join('\n');
const tekstNaarLijst = (t) => t.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

export default function TeamsKanaalSjabloon() {
  const [sjabloon, setSjabloon] = useState(null);
  const [mappen, setMappen] = useState('');
  const [kopjes, setKopjes] = useState('');
  const [leden, setLeden] = useState('');
  const [melden, setMelden] = useState('');
  const [waarschuwing, setWaarschuwing] = useState(180);
  const [grens, setGrens] = useState(200);
  const [lijst, setLijst] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState(null);

  const laad = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([authFetch(`${API}/sjabloon`), authFetch(`${API}/lijst`)]);
      if (s.ok) {
        const d = (await s.json()).sjabloon || {};
        setSjabloon(d);
        setMappen(lijstNaarTekst(d.mappen));
        setKopjes(lijstNaarTekst(d.kopjes));
        setLeden(lijstNaarTekst(d.vaste_leden));
        setMelden(lijstNaarTekst(d.melding_team_ontbreekt));
        setWaarschuwing(d.kanaalgrens_waarschuwing ?? 180);
        setGrens(d.kanaalgrens ?? 200);
      }
      if (l.ok) setLijst(await l.json());
    } catch (e) { setMelding({ type: 'error', tekst: e.message }); }
  }, []);

  useEffect(() => { laad(); }, [laad]);

  const opslaan = async () => {
    setBezig(true); setMelding(null);
    try {
      const r = await authFetch(`${API}/sjabloon`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sjabloon: {
          ...sjabloon, mappen: tekstNaarLijst(mappen),
          // Per regel (een kopje mag een komma bevatten).
          kopjes: kopjes.split('\n').map((x) => x.trim()).filter(Boolean),
          vaste_leden: tekstNaarLijst(leden), melding_team_ontbreekt: tekstNaarLijst(melden),
          kanaalgrens_waarschuwing: Number(waarschuwing), kanaalgrens: Number(grens),
        } }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setMelding({ type: 'success', tekst: 'Opgeslagen — geldt voor het volgende kanaal.' });
      await laad();
    } catch (e) { setMelding({ type: 'error', tekst: e.message }); } finally { setBezig(false); }
  };

  const rondeNu = async () => {
    if (lijst?.modus === 'on' && !(await bevestig({
      titel: 'Ronde nu draaien',
      tekst: 'De automaat loopt nu alle open projecten na: in de stand Aan kan dat kanalen aanmaken en collega\'s aan klantteams toevoegen.',
      knop: 'Draaien',
    }))) return;
    setBezig(true); setMelding(null);
    try {
      const r = await authFetch(`${API}/ronde`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setMelding({ type: 'info', tekst: `Ronde klaar: ${d.verwerkt || 0} project(en) nagelopen.` });
      await laad();
    } catch (e) { setMelding({ type: 'error', tekst: e.message }); } finally { setBezig(false); }
  };

  if (!sjabloon) return <CircularProgress size={22} />;
  return (
    <Box sx={{ maxWidth: 1100 }}>
      {lijst && (
        <Alert severity={lijst.modus === 'on' ? 'success' : 'info'} sx={{ mb: 2 }}>
          {MODUS_TEKST[lijst.modus] || lijst.modus}
          {lijst.modus === 'on' && !lijst.schrijf_app && ' De schrijf-app is nog niet ingesteld: er wordt niets aangemaakt.'}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Mappen in de Bestanden-map van elk nieuw kanaal — één per regel, submap met een schuine streep
        (<code>POSTPRODUCTIE/v1</code>).
      </Typography>
      <TextField fullWidth multiline minRows={6} maxRows={16} value={mappen} onChange={(e) => setMappen(e.target.value)}
        inputProps={{ 'aria-label': 'Mappen' }} />
      <TextField fullWidth multiline minRows={3} maxRows={10} sx={{ mt: 2 }} value={kopjes}
        onChange={(e) => setKopjes(e.target.value)} label="Fase-kopjes in elk nieuw kanaal"
        helperText="Eén per regel, in deze volgorde. Ze komen als berichten in het kanaal, namens de PL die het kanaal aanmaakt. Leeg = geen kopjes."
        inputProps={{ 'aria-label': 'Fase-kopjes' }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <TextField label="Altijd lid van het klantteam" helperText="Alleen @fullframe.nl, één per regel"
          multiline minRows={2} value={leden} onChange={(e) => setLeden(e.target.value)} />
        <TextField label="Melding als er geen klantteam is" helperText="Naast de PL; alleen @fullframe.nl"
          multiline minRows={2} value={melden} onChange={(e) => setMelden(e.target.value)} />
        <TextField label="Waarschuwen vanaf (kanalen per team)" type="number" value={waarschuwing}
          onChange={(e) => setWaarschuwing(e.target.value)} />
        <TextField label="Niet meer aanmaken vanaf (kanalen per team)" type="number" value={grens}
          onChange={(e) => setGrens(e.target.value)} />
      </Box>
      {melding && <Alert severity={melding.type} sx={{ mt: 1.5 }}>{melding.tekst}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Button variant="contained" onClick={opslaan} disabled={bezig} sx={{ textTransform: 'none', fontWeight: 700 }}>Opslaan</Button>
        <Button variant="text" onClick={rondeNu} disabled={bezig || lijst?.modus === 'off'} sx={{ textTransform: 'none' }}>Ronde nu draaien</Button>
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 4, mb: 1 }}>Recente projecten</Typography>
      {!lijst?.rijen?.length ? (
        <Typography variant="body2" color="text.secondary">Nog niets — de eerste geaccepteerde offerte verschijnt hier.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell><TableCell>Status</TableCell><TableCell>Team</TableCell>
              <TableCell>Kanaal</TableCell><TableCell>Toelichting</TableCell><TableCell>Bijgewerkt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lijst.rijen.map((r) => (
              <TableRow key={r.project_number}>
                <TableCell>{r.project_number} {r.project_name}</TableCell>
                <TableCell><Chip size="small" label={teamsKanaalStatusLabel(r)} color={teamsKanaalStatusKleur(r.status)} /></TableCell>
                <TableCell>{r.team_name || r.plan?.team?.name || '—'}</TableCell>
                <TableCell>
                  {r.web_url ? <a href={r.web_url} target="_blank" rel="noreferrer">{r.channel_name}</a> : (r.channel_name || r.plan?.naam || '—')}
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  {r.fout || (r.plan?.leden?.toevoegen?.length ? `erbij: ${r.plan.leden.toevoegen.join(', ')}` : '')}
                  {(r.plan?.waarschuwingen || []).map((w) => <div key={w} style={{ color: '#b45309' }}>⚠ {w}</div>)}
                </TableCell>
                <TableCell>{(r.updated_at || '').slice(0, 16).replace('T', ' ')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
